import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Nfc, Zap, RefreshCw, CheckCircle2, ClipboardList, Plus, Clock, UserRound, Search, Ban, PauseCircle, PlayCircle, X, QrCode } from "lucide-react";
import { PageLoading, PageHeader } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";

const STATUS_C = {
  ACTIVE: "bg-emerald-50 text-emerald-600", GENERATED: "bg-slate-100 text-slate-500",
  PROGRAMMED: "bg-sky-50 text-sky-600", VERIFIED: "bg-indigo-50 text-indigo-600",
  REVOKED: "bg-red-50 text-red-600", SUSPENDED: "bg-amber-50 text-amber-700",
  REQUESTED: "bg-slate-100 text-slate-500", REPLACED: "bg-slate-100 text-slate-400", DAMAGED: "bg-red-50 text-red-500",
};
const TYPE_META = {
  CARE_TIME_TAG: { label: "Pflege Zeiterfassung", icon: Clock, desc: "Dienst starten / beenden", color: "text-cyan-dark" },
  CARE_PATIENT_VISIT_TAG: { label: "Patientenbesuch", icon: UserRound, desc: "Patientenbesuch starten / beenden", color: "text-navy" },
};

function CreateWizard({ onClose, onDone, prefill }) {
  const [step, setStep] = useState(prefill?.tag_type ? 2 : 1);
  const [type, setType] = useState(prefill?.tag_type || null);
  const [orgs, setOrgs] = useState([]);
  const [orgId, setOrgId] = useState(prefill?.organization_id || "");
  const [cfg, setCfg] = useState({ location_name: "", city: "", verification_radius_meters: 200, quantity: 1, note: "" });
  const [pq, setPq] = useState("");
  const [patients, setPatients] = useState([]);
  const [patient, setPatient] = useState(prefill?.patient_id ? { id: prefill.patient_id, patient_number: prefill.patient_number, name: "", locations: [] } : null);
  const [locId, setLocId] = useState(prefill?.patient_location_id || "");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!type) return;
    api.get(`/admin/care/eligible-orgs?tag_type=${type}`).then((r) => setOrgs(r.data.organizations || [])).catch(() => setOrgs([]));
  }, [type]);
  useEffect(() => {
    if (type !== "CARE_PATIENT_VISIT_TAG" || !orgId) return;
    const h = setTimeout(() => api.get(`/admin/care/orgs/${orgId}/patients?q=${encodeURIComponent(pq)}`).then((r) => setPatients(r.data.patients || [])).catch(() => setPatients([])), 250);
    return () => clearTimeout(h);
  }, [type, orgId, pq]);

  const submit = async () => {
    setBusy(true);
    try {
      const body = { tag_type: type, organization_id: orgId, order_id: prefill?.order_id,
        verification_radius_meters: Number(cfg.verification_radius_meters) || 200 };
      if (type === "CARE_TIME_TAG") Object.assign(body, { location_name: cfg.location_name, city: cfg.city, quantity: Number(cfg.quantity) || 1 });
      else Object.assign(body, { patient_id: patient?.id, patient_location_id: locId || undefined });
      const r = await api.post("/admin/care/tags/generate", body);
      setResult(r.data.created || []); onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler bei der Erstellung"); } finally { setBusy(false); }
  };
  const activateAll = async () => {
    try { await Promise.all(result.map((c) => api.post(`/admin/care/tags/${c.id}/status`, { status: "ACTIVE" }))); toast.success("Tag(s) aktiviert"); onDone?.(); onClose(); }
    catch { toast.error("Aktivierung fehlgeschlagen"); }
  };

  const steps = ["Tag-Typ", "Unternehmen", "Konfiguration", "Fertig"];
  const canNext = step === 1 ? !!type : step === 2 ? !!orgId : step === 3 ? (type === "CARE_TIME_TAG" ? !!cfg.location_name : !!patient && (patient.locations?.length ? !!locId : true)) : true;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="caretag-wizard">
        <DialogHeader><DialogTitle className="font-heading text-navy">Pflege Tag erstellen</DialogTitle></DialogHeader>
        {/* stepper */}
        <div className="mb-4 flex items-center gap-1">
          {steps.map((s, i) => (
            <div key={s} className="flex flex-1 items-center gap-1">
              <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${step >= i + 1 ? "bg-cyan text-white" : "bg-slate-100 text-slate-400"}`}>{i + 1}</span>
              <span className={`truncate text-[11px] ${step >= i + 1 ? "text-navy" : "text-slate-400"}`}>{s}</span>
            </div>
          ))}
        </div>

        {step === 1 && (
          <div className="grid grid-cols-2 gap-3" data-testid="wizard-step-type">
            {Object.entries(TYPE_META).map(([k, m]) => {
              const Icon = m.icon;
              return (
                <button key={k} onClick={() => { setType(k); }} data-testid={`wizard-type-${k}`}
                  className={`rounded-2xl border p-4 text-left transition-all ${type === k ? "border-cyan bg-cyan/5 ring-2 ring-cyan/30" : "border-slate-200 hover:border-cyan/40"}`}>
                  <Icon className={`mb-2 h-6 w-6 ${m.color}`} />
                  <div className="font-heading text-sm font-bold text-navy">{m.label}</div>
                  <div className="text-xs text-slate-500">{m.desc}</div>
                </button>
              );
            })}
          </div>
        )}

        {step === 2 && (
          <div data-testid="wizard-step-org">
            <label className="mb-1 block text-xs font-semibold text-slate-500">Unternehmen (CARE, berechtigt)</label>
            <select value={orgId} onChange={(e) => setOrgId(e.target.value)} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid="wizard-org-select">
              <option value="">— Unternehmen wählen —</option>
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
            {orgs.length === 0 && <p className="mt-2 text-xs text-amber-600">Keine berechtigten Unternehmen für diesen Tag-Typ (CARE_MODULE{type === "CARE_TIME_TAG" ? " + Zeiterfassung" : ""} erforderlich).</p>}
          </div>
        )}

        {step === 3 && type === "CARE_TIME_TAG" && (
          <div className="space-y-3" data-testid="wizard-step-config-time">
            <div><label className="mb-1 block text-xs font-semibold text-slate-500">Bezeichnung / Standort *</label>
              <Input value={cfg.location_name} onChange={(e) => setCfg({ ...cfg, location_name: e.target.value })} placeholder="z.B. Hauptniederlassung Essen – Haupteingang" data-testid="wizard-location-name" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-1"><label className="mb-1 block text-xs font-semibold text-slate-500">Stadt</label><Input value={cfg.city} onChange={(e) => setCfg({ ...cfg, city: e.target.value })} /></div>
              <div className="col-span-1"><label className="mb-1 block text-xs font-semibold text-slate-500">Radius (m)</label><Input type="number" value={cfg.verification_radius_meters} onChange={(e) => setCfg({ ...cfg, verification_radius_meters: e.target.value })} /></div>
              <div className="col-span-1"><label className="mb-1 block text-xs font-semibold text-slate-500">Menge</label><Input type="number" min={1} max={20} value={cfg.quantity} onChange={(e) => setCfg({ ...cfg, quantity: e.target.value })} data-testid="wizard-quantity" /></div>
            </div>
            <p className="text-[11px] text-slate-400">Zeiterfassungs-Tags werden dem Unternehmen/Standort zugewiesen – niemals einem Patienten oder Mitarbeiter.</p>
          </div>
        )}

        {step === 3 && type === "CARE_PATIENT_VISIT_TAG" && (
          <div className="space-y-3" data-testid="wizard-step-config-patient">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <Input value={pq} onChange={(e) => setPq(e.target.value)} placeholder="Patient suchen (Name oder Patient-ID)" className="pl-9" data-testid="wizard-patient-search" />
            </div>
            <div className="max-h-44 overflow-y-auto rounded-xl border border-slate-100">
              {patients.map((p) => (
                <button key={p.id} onClick={() => { setPatient(p); setLocId(p.locations?.find((l) => l.is_primary)?.id || p.locations?.[0]?.id || ""); }}
                  className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-50 ${patient?.id === p.id ? "bg-cyan/5" : ""}`} data-testid={`wizard-patient-${p.id}`}>
                  <span className="text-navy">{p.name}</span>
                  <span className="font-mono text-[11px] text-slate-400">{p.patient_number}</span>
                </button>
              ))}
              {patients.length === 0 && <div className="px-3 py-4 text-center text-xs text-slate-400">Keine Patienten gefunden.</div>}
            </div>
            {patient && patient.locations?.length > 0 && (
              <div><label className="mb-1 block text-xs font-semibold text-slate-500">Patientenstandort *</label>
                <select value={locId} onChange={(e) => setLocId(e.target.value)} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid="wizard-location-select">
                  {patient.locations.map((l) => <option key={l.id} value={l.id}>{l.label}{l.is_primary ? " (primär)" : ""}</option>)}
                </select></div>
            )}
            <p className="text-[11px] text-slate-400">Der physische Tag speichert KEINE Patientendaten – nur ein sicheres Token.</p>
          </div>
        )}

        {step === 4 && result && (
          <div className="space-y-3" data-testid="wizard-step-done">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4 text-sm text-emerald-700"><CheckCircle2 className="mb-1 h-5 w-5" /> {result.length} Tag(s) erstellt (Status: Erstellt). Aktivieren Sie sie, um sie scanbar zu machen.</div>
            <div className="space-y-1">
              {result.map((c) => (
                <div key={c.id} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2 text-sm">
                  <span className="font-mono text-xs text-navy">{c.display_tag_number}</span>
                  <span className="truncate pl-3 text-[11px] text-slate-400">{c.nfc_url}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-5 flex justify-between">
          <Button variant="outline" className="rounded-full" onClick={() => (step <= 1 || step === 4 ? onClose() : setStep(step - 1))}>{step === 4 ? "Schließen" : step <= 1 ? "Abbrechen" : "Zurück"}</Button>
          {step < 3 && <Button className="rounded-full bg-cyan text-white hover:bg-cyan-dark" disabled={!canNext} onClick={() => setStep(step + 1)} data-testid="wizard-next">Weiter</Button>}
          {step === 3 && <Button className="rounded-full bg-cyan text-white hover:bg-cyan-dark" disabled={!canNext || busy} onClick={() => { submit().then(() => setStep(4)); }} data-testid="wizard-generate">{busy ? "Erstelle…" : "Tag generieren"}</Button>}
          {step === 4 && <Button className="rounded-full bg-emerald-500 text-white hover:bg-emerald-600" onClick={activateAll} data-testid="wizard-activate-all">Alle aktivieren</Button>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DetailDrawer({ tag, onClose, onAction }) {
  const [qr, setQr] = useState(null);
  useEffect(() => {
    setQr(null);
    if (tag?.id) api.get(`/admin/care/tags/${tag.id}/qr`, { responseType: "blob" }).then((r) => setQr(URL.createObjectURL(r.data))).catch(() => {});
  }, [tag]);
  if (!tag) return null;
  const meta = TYPE_META[tag.tag_type] || {};
  const Row = ({ k, v }) => <div className="flex justify-between gap-4 py-1.5 text-sm"><span className="text-slate-400">{k}</span><span className="text-right text-navy">{v || "—"}</span></div>;
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-navy/30" onClick={onClose} data-testid="caretag-detail">
      <div className="h-full w-full max-w-md overflow-y-auto bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between"><h3 className="font-heading text-lg font-bold text-navy">{tag.display_tag_number}</h3><button onClick={onClose}><X className="h-5 w-5 text-slate-400" /></button></div>
        {qr && <img src={qr} alt="QR" className="mx-auto mb-4 h-40 w-40 rounded-xl border border-slate-100 p-2" data-testid="caretag-qr" />}
        <Row k="Typ" v={meta.label} />
        <Row k="Unternehmen" v={tag.organization_name} />
        <Row k="Kontext" v={tag.tag_type === "CARE_PATIENT_VISIT_TAG" ? tag.patient_number : tag.location_name} />
        <Row k="Status" v={tag.status_label} />
        <Row k="Hardware" v={`${tag.reader_family || "PN532"} · ${tag.chip_type || "NTAG215"}`} />
        <Row k="Erstellt" v={tag.created_at?.slice(0, 10)} />
        <Row k="Aktiviert" v={tag.activated_at?.slice(0, 10)} />
        <Row k="Letzter Scan" v={tag.last_used_at?.slice(0, 16)?.replace("T", " ")} />
        <div className="mt-5 flex flex-wrap gap-2">
          {tag.status !== "ACTIVE" && tag.status !== "REVOKED" && <Button size="sm" className="rounded-full bg-emerald-500 text-white hover:bg-emerald-600" onClick={() => onAction(tag.id, "status", "ACTIVE")}><PlayCircle className="mr-1 h-4 w-4" />Aktivieren</Button>}
          {tag.status === "ACTIVE" && <Button size="sm" variant="outline" className="rounded-full" onClick={() => onAction(tag.id, "status", "SUSPENDED")}><PauseCircle className="mr-1 h-4 w-4" />Sperren</Button>}
          {tag.status === "SUSPENDED" && <Button size="sm" className="rounded-full bg-emerald-500 text-white" onClick={() => onAction(tag.id, "status", "ACTIVE")}><PlayCircle className="mr-1 h-4 w-4" />Entsperren</Button>}
          {tag.status !== "REVOKED" && <Button size="sm" variant="outline" className="rounded-full text-cyan-dark" onClick={() => onAction(tag.id, "rotate")}><RefreshCw className="mr-1 h-4 w-4" />Token rotieren</Button>}
          {tag.status !== "REVOKED" && <Button size="sm" variant="outline" className="rounded-full text-red-500" onClick={() => onAction(tag.id, "status", "REVOKED")}><Ban className="mr-1 h-4 w-4" />Widerrufen</Button>}
        </div>
      </div>
    </div>
  );
}

export default function AdminCareTags() {
  const [data, setData] = useState(null);
  const [wizard, setWizard] = useState(null);
  const [detail, setDetail] = useState(null);
  const [f, setF] = useState({ type: "", status: "", q: "" });
  const load = () => api.get("/admin/care/tags").then((r) => setData(r.data)).catch(() => setData({ tags: [], orders: [] }));
  useEffect(() => { load(); }, []);

  const action = async (id, kind, status) => {
    try {
      if (kind === "status") await api.post(`/admin/care/tags/${id}/status`, { status });
      else if (kind === "rotate") { const r = await api.post(`/admin/care/tags/${id}/rotate`); toast.success(`Token rotiert (v${r.data.token_version})`); }
      if (kind === "status") toast.success(status === "ACTIVE" ? "Aktiviert" : status === "REVOKED" ? "Widerrufen" : "Aktualisiert");
      setDetail(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };

  const tags = useMemo(() => (data?.tags || []).filter((t) =>
    (!f.type || t.tag_type === f.type) && (!f.status || t.status === f.status) &&
    (!f.q || [t.display_tag_number, t.organization_name, t.patient_number, t.location_name].some((v) => (v || "").toLowerCase().includes(f.q.toLowerCase())))), [data, f]);

  if (!data) return <PageLoading />;
  return (
    <div data-testid="admin-caretags-page">
      <PageHeader icon={Nfc} title="Pflege NFC / QR Provisioning"
        action={<Button className="rounded-full bg-cyan text-white hover:bg-cyan-dark" onClick={() => setWizard({})} data-testid="caretag-create-btn"><Plus className="mr-1 h-4 w-4" /> Pflege Tag erstellen</Button>} />

      {/* Orders */}
      <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-5" data-testid="admin-caretag-orders">
        <div className="mb-3 flex items-center gap-2"><ClipboardList className="h-4.5 w-4.5 text-cyan-dark" /><h3 className="font-heading font-bold text-navy">Bestellungen / Anfragen</h3></div>
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-slate-400"><tr><th className="py-2">Firma</th><th>Typ</th><th>Kontext</th><th>Menge</th><th>Status</th><th></th></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {data.orders.map((o) => (
              <tr key={o.id} data-testid={`admin-order-${o.id}`}>
                <td className="py-2 text-navy">{o.organization_name}</td><td>{o.tag_type_label}</td>
                <td className="text-slate-500">{o.patient_number || o.location_name || "—"}</td><td>{o.quantity}</td>
                <td><span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">{o.status}</span></td>
                <td>{o.status === "REQUESTED" && <Button size="sm" className="rounded-full bg-cyan text-white hover:bg-cyan-dark" onClick={() => setWizard({ tag_type: o.tag_type, organization_id: o.organization_id, order_id: o.id, patient_id: o.patient_id, patient_number: o.patient_number, patient_location_id: o.patient_location_id })} data-testid={`admin-fulfill-${o.id}`}><Zap className="mr-1 h-3.5 w-3.5" /> Tags anlegen</Button>}</td>
              </tr>
            ))}
            {data.orders.length === 0 && <tr><td colSpan={6} className="py-4 text-center text-slate-400">Keine offenen Bestellungen.</td></tr>}
          </tbody>
        </table></div>
      </div>

      {/* Filters */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]"><Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" /><Input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Tag ID, Firma, Patient-ID, Standort…" className="pl-9" data-testid="caretag-search" /></div>
        <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className="rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid="caretag-filter-type"><option value="">Alle Typen</option>{Object.entries(TYPE_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}</select>
        <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className="rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid="caretag-filter-status"><option value="">Alle Status</option>{["GENERATED", "ACTIVE", "SUSPENDED", "REVOKED"].map((s) => <option key={s} value={s}>{s}</option>)}</select>
      </div>

      {/* Tags table / empty state */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="admin-caretag-list">
        {tags.length === 0 ? (
          <div className="flex flex-col items-center gap-3 px-4 py-14 text-center" data-testid="caretag-empty">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-cyan/10"><QrCode className="h-7 w-7 text-cyan-dark" /></span>
            <div className="font-heading font-bold text-navy">Noch keine Pflege Tags vorhanden.</div>
            <p className="max-w-sm text-sm text-slate-500">Erstellen und konfigurieren Sie NFC/QR Tags für Pflege-Zeiterfassung oder Patientenbesuche.</p>
            <Button className="rounded-full bg-cyan text-white hover:bg-cyan-dark" onClick={() => setWizard({})} data-testid="caretag-empty-cta"><Plus className="mr-1 h-4 w-4" /> Pflege Tag erstellen</Button>
          </div>
        ) : (
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-400"><tr><th className="px-4 py-3">Tag ID</th><th className="px-4 py-3">Firma</th><th className="px-4 py-3">Typ</th><th className="px-4 py-3">Kontext</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Letzter Scan</th><th className="px-4 py-3">Aktion</th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {tags.map((t) => (
                <tr key={t.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setDetail(t)} data-testid={`admin-caretag-${t.id}`}>
                  <td className="px-4 py-3 font-mono text-xs text-slate-600">{t.display_tag_number}</td>
                  <td className="px-4 py-3 text-navy">{t.organization_name}</td>
                  <td className="px-4 py-3 text-slate-600">{t.tag_type_label}</td>
                  <td className="px-4 py-3 text-slate-500">{t.tag_type === "CARE_PATIENT_VISIT_TAG" ? t.patient_number : (t.location_name || "—")}</td>
                  <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_C[t.status] || "bg-slate-100 text-slate-500"}`}>{t.status_label}</span></td>
                  <td className="px-4 py-3 text-xs text-slate-400">{t.last_used_at ? t.last_used_at.slice(0, 16).replace("T", " ") : "—"}</td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-2">
                      {t.status === "GENERATED" && <button onClick={() => action(t.id, "status", "ACTIVE")} title="Aktivieren" className="text-emerald-600" data-testid={`admin-activate-${t.id}`}><CheckCircle2 className="h-4 w-4" /></button>}
                      {t.status !== "REVOKED" && <button onClick={() => action(t.id, "rotate")} title="Token rotieren" className="text-cyan-dark" data-testid={`admin-rotate-${t.id}`}><RefreshCw className="h-4 w-4" /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>

      {wizard && <CreateWizard prefill={wizard} onClose={() => setWizard(null)} onDone={load} />}
      {detail && <DetailDrawer tag={detail} onClose={() => setDetail(null)} onAction={action} />}
    </div>
  );
}
